import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminPartnerService } from '../services/admin-partner.service';
import type { PartnerLevel } from '../types/admin-partner';

export function useAdminPartners(params?: {
  page?: number;
  limit?: number;
  status?: string;
  kycStatus?: string;
  level?: string;
  search?: string;
  from?: string;
  to?: string;
}) {
  return useQuery({
    queryKey: ['admin', 'partners', 'list', params],
    queryFn: () => adminPartnerService.getPartners(params),
  });
}

export function useAdminPartner(id: string) {
  return useQuery({
    queryKey: ['admin', 'partners', id],
    queryFn: () => adminPartnerService.getPartnerById(id),
    enabled: !!id,
  });
}

export function useApprovePartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminPartnerService.approvePartner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] });
    },
  });
}

export function useRejectPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      adminPartnerService.rejectPartner(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] });
    },
  });
}

export function useSuspendPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      adminPartnerService.suspendPartner(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] });
    },
  });
}

export function useActivatePartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminPartnerService.activatePartner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] });
    },
  });
}

export function useChangePartnerLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, level }: { id: string; level: PartnerLevel }) =>
      adminPartnerService.changePartnerLevel(id, level),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] });
    },
  });
}
